import { useCallback, useMemo } from "react";

import { useSessionStorageOverrides } from "~/shared/hooks/use-session-storage-overrides.ts";
import type { BoardColumn } from "~/shared/types/quote-board-types.ts";

/**
 * Persists per-column visibility selections in sessionStorage so users keep
 * their column choices on each page that uses a column chooser.
 *
 * Stores only a `Record<field, boolean>` of overrides keyed by column field.
 * The supplied `defaultColumns` (which may change over time, e.g. when their
 * cell renderers depend on loading state) are merged with the persisted
 * overrides on every render.
 *
 * @param storageKey  Stable key identifying this page's column chooser.
 * @param defaultColumns  Default column definitions for the page.
 */
export const useColumnSelections = (
	storageKey: string,
	defaultColumns: BoardColumn[],
) => {
	const { overrides, setOverride, resetOverrides } =
		useSessionStorageOverrides(storageKey);

	const columns = useMemo<BoardColumn[]>(
		() =>
			defaultColumns.map((col) => {
				if (col.required) {
					return col.show === true ? col : { ...col, show: true };
				}
				const field = col.field;
				if (field && Object.prototype.hasOwnProperty.call(overrides, field)) {
					return { ...col, show: overrides[field] === true };
				}
				return col;
			}),
		[defaultColumns, overrides],
	);

	const toggleColumn = useCallback(
		(column: BoardColumn) => {
			const field = column.field;
			if (!field) return;
			setOverride(field, !!column.show);
		},
		[setOverride],
	);

	return { columns, toggleColumn, resetColumns: resetOverrides, setOverride };
};
